import { useEffect, useRef, useState } from "react";

/* PWA update toast (polish P3). Owns service-worker registration. When a new
   deploy installs a worker that's waiting to take over, it shows a "new version"
   toast; clicking Reload activates the waiting worker and reloads once so the
   fresh hashed assets load. First install (no prior controller) never prompts. */
export default function UpdatePrompt() {
  const [ready, setReady] = useState(false);
  const waitingRef = useRef(null);
  const reloadingRef = useRef(false);

  useEffect(() => {
    if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
    let reg;

    const onControllerChange = () => {
      // Only reload when the user accepted an update — not on the first-install
      // claim (which also fires controllerchange).
      if (reloadingRef.current) window.location.reload();
    };

    const watch = (worker) => {
      if (!worker) return;
      worker.addEventListener("statechange", () => {
        if (worker.state === "installed" && navigator.serviceWorker.controller) {
          waitingRef.current = worker;
          setReady(true);
        }
      });
    };

    navigator.serviceWorker
      .register("/sw.js")
      .then((r) => {
        reg = r;
        if (r.waiting && navigator.serviceWorker.controller) {
          waitingRef.current = r.waiting;
          setReady(true);
        }
        r.addEventListener("updatefound", () => watch(r.installing));
      })
      .catch(() => {});

    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
    const onVis = () => {
      if (document.visibilityState === "visible" && reg) reg.update().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  if (!ready) return null;
  const reload = () => {
    reloadingRef.current = true;
    waitingRef.current?.postMessage("SKIP_WAITING");
  };
  // Dismiss hides the toast for this session (reloading mid-shoe is risky —
  // resume only covers the last-saved snapshot). The waiting worker stays
  // parked and re-prompts on the next full page load.
  const dismiss = () => setReady(false);
  return (
    <div className="pwa-toast" role="status">
      <span>A new version is available.</span>
      <button className="pwa-toast-btn" onClick={reload}>
        Reload
      </button>
      <button className="pwa-toast-btn pwa-toast-dismiss" onClick={dismiss} aria-label="Dismiss update notice">
        Later
      </button>
    </div>
  );
}
